import { useState, useEffect, useMemo, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Menu,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  Person as PersonIcon,
  Apartment as ApartmentIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  CalendarMonth as CalendarMonthIcon,
  RemoveRedEyeOutlined as ViewIcon,
  SwapVert as SwapVertIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../contexts/DataProvider";
import { useAlert } from "../functions/useAlert";
import {
  deleteContactGraph,
  deleteClientGraph,
} from "../functions/knowledgeGraphApi";
import { deleteClient } from "../services/clients.service";
import { unlinkContact } from "../services/relationships.service";
import Container from "../base/Container";
import NoDataFound from "../components/common/NoDataFound";
import EmptyState from "../components/common/EmptyState";
import TableSkeleton from "../components/common/TableSkeleton";
import StatCard from "../components/common/StatCard";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ResizeHandle from "../components/common/ResizeHandle";
import { useResizableListPanel } from "../hooks/useResizableListPanel";
import ClientModal from "../components/modals/ClientModal";
import ContactModal from "../components/modals/ContactModal";
import UnlinkConfirmationDialog from "../components/modals/UnlinkConfirmationDialog";
import ClientLinkContactModal from "../components/client/ClientLinkContactModal";
import ClientDetailPanel from "../components/client/ClientDetailPanel";
import { Client } from "../components/client/types";
import { getRenewalStatus, getClientReferenceDate } from "../utils/clientRenewal";
import { useCRMSort, sortByCRMPref, CRM_SORT_DEFAULT, CRM_SORT_OPTIONS } from "../hooks/useCRMSort";
import { useCRMSelection } from "../hooks/useCRMSelection";
import { useClients } from "../hooks/useClients";
import { useClientContacts } from "../hooks/useClientContacts";
import { useClientModals } from "../hooks/useClientModals";
import { useClientListPagination } from "../hooks/useClientListPagination";
import { useClientPageAIModalBridge } from "../hooks/useClientPageAIModalBridge";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { useSessionState } from "../hooks/useSessionState";

const ClientsPage = () => {
  const { login } = useContext(DataContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useSessionState("clients:searchQuery", "");
  const [entityTypeFilter, setEntityTypeFilter] = useSessionState("clients:entityTypeFilter", "All");
  const [setAlert] = useAlert();

  const { clients, isLoading: isClientsLoading, refetch: fetchClientsData } = useClients(login.data.orgId);

  // selectedClient itself stays plain state (holds full, possibly-stale-until-refetch client
  // data), but its id is persisted so it can be re-resolved against the fresh `clients` list
  // once that loads — same restore-on-load pattern the existing re-sync effect below already used.
  const [selectedClientId, setSelectedClientId] = useSessionState<string | null>("clients:selectedClientId", null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  // Restoring selectedClient from selectedClientId is a one-shot "resume where I left off"
  // action for this mount. Without this guard, selectedClient becoming null later for any
  // other reason (e.g. the selected client being deleted, which clears selectedClient but not
  // the persisted selectedClientId) reads as indistinguishable from "not restored yet" and gets
  // re-selected. Restore, however it turns out, is only ever attempted once per mount.
  const hasRestoredSelectionRef = useRef(false);
  // True only while there's a persisted selection still waiting to be resolved against the
  // freshly-fetched client list — lets the panel show a loading placeholder instead of
  // flashing "Select a Client" for the split second before the restore completes.
  const [isRestoringSelection, setIsRestoringSelection] = useState(() => !!selectedClientId);
  const {
    contacts: clientContacts,
    isLoading: isContactsLoading,
    fetchFor: fetchClientContacts,
  } = useClientContacts();

  const {
    isClientModalOpen, setIsClientModalOpen,
    isContactModalOpen, setIsContactModalOpen,
    isDeleteClientDialogOpen, setIsDeleteClientDialogOpen,
    isLinkModalOpen, setIsLinkModalOpen,
    isUnlinkConfirmOpen, setIsUnlinkConfirmOpen,
    clientForModal, setClientForModal,
    contactForModal, setContactForModal,
    returnToLinkMode, setReturnToLinkMode,
    newlyCreatedContact, setNewlyCreatedContact,
    currentRelationshipData, setCurrentRelationshipData,
  } = useClientModals();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const {
    selectedClientIds,
    setSelectedClientIds,
    handleSelectAllClients,
    handleSelectClient: handleToggleClientSelection,
  } = useCRMSelection();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  // Below md the list and detail panes stack vertically, so there is nothing to drag.
  const isStacked = useMediaQuery(theme.breakpoints.down("md"));
  const {
    width: listWidth,
    panelRef: listPanelRef,
    handleResizeStart,
  } = useResizableListPanel({
    storageKey: "clients:listWidth",
    defaultWidth: 420,
    minWidth: 300,
    maxWidth: 720,
  });

  const [clientSortPref, setClientSortPref] = useCRMSort();
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    localStorage.setItem("prospoClientSortPref", JSON.stringify(clientSortPref));
  }, [clientSortPref]);

  useClientPageAIModalBridge({ setClientForModal, setIsClientModalOpen, navigate });

  const handleSelectClient = (client: Client) => {
    // Clicking the already-selected row is a no-op, not a toggle: row click means "show me
    // this one", separate from the per-row checkboxes' bulk-action selection. Toggling off
    // here made the panel disappear for the client the user was actively viewing.
    if (selectedClient?._id === client._id) return;
    setSelectedClient(client);
    setSelectedClientId(client._id);
    fetchClientContacts(client._id);
  };

  const handleSwitchToCreateContact = () => {
    setIsLinkModalOpen(false); // Close Link Modal
    setContactForModal(null); // Clear any edit data (ensure Create Mode)
    setIsContactModalOpen(true); // Open Contact Modal
    setReturnToLinkMode(true); // Set flag to return later
    setNewlyCreatedContact(null); // Clear any previous selection
  };

  const handleOpenModal = (type: string, data?: any) => {
    if (type === "link" || type === "deleteClient") {
      setClientForModal(data);
    }

    // Update for Client Edit
    if (type === "editClient") {
      setClientForModal(data);
      setIsClientModalOpen(true);
    }

    // Update for Contact Edit
    if (type === "editContact") {
      setContactForModal(data);
      // Set relationship data if available
      if (data.relationshipId && selectedClient) {
        setCurrentRelationshipData({
          relationshipId: data.relationshipId,
          relationshipType: data.relationshipType,
          isPrimary: data.isPrimary,
          clientId: selectedClient._id,
        });
      } else {
        setCurrentRelationshipData(null);
      }
      setIsContactModalOpen(true);
    }

    if (type === "unlinkContact") {
      setContactForModal(data);
      setIsUnlinkConfirmOpen(true);
    }

    if (type === "link") setIsLinkModalOpen(true);
    if (type === "deleteClient") setIsDeleteClientDialogOpen(true);
    if (type === "viewClient") navigate(`/client/${data._id}`);
  };

  const handleDeleteClient = async () => {
    if (!clientForModal) return;
    const ctx = { userId: login?.data?.id, orgId: login?.data?.orgId };
    try {
      if (selectedClientIds.length > 1) {
        await Promise.all(
          selectedClientIds.map((clientId) => deleteClient(clientId)),
        );
        selectedClientIds.forEach((id) =>
          deleteClientGraph(id, ctx).catch(() => { }),
        );
        setAlert(
          `${selectedClientIds.length} clients deleted successfully`,
          "success",
        );
        setSelectedClientIds([]);
      } else if (selectedClientIds.length === 1) {
        await deleteClient(selectedClientIds[0]);
        deleteClientGraph(selectedClientIds[0], ctx).catch(() => { });
        setAlert("Client deleted successfully", "success");
        setSelectedClientIds([]);
      } else {
        await deleteClient(clientForModal._id);
        deleteClientGraph(clientForModal._id, ctx).catch(() => { });
        setAlert("Client deleted successfully", "success");
      }

      fetchClientsData();
      setSelectedClient(null);
    } catch (error) {
      setAlert("Failed to delete client(s)", "error");
    } finally {
      setIsDeleteClientDialogOpen(false);
    }
  };

  const handleUnlinkContact = async () => {
    if (!contactForModal || !selectedClient) return;
    try {
      const unlinkPayload = {
        clientId: selectedClient._id,
        contactId: contactForModal._id,
      };
      await unlinkContact(unlinkPayload);
      deleteContactGraph({
        ...unlinkPayload,
        _id: unlinkPayload.contactId,
      }).catch(() => { });
      setAlert("Contact unlinked successfully", "success");
      fetchClientContacts(selectedClient._id);
    } catch (error) {
      setAlert("Failed to unlink contact", "error");
    } finally {
      setIsUnlinkConfirmOpen(false);
    }
  };

  const filteredClients = useMemo(() => {
    const filtered = clients.filter((client) => {
      const matchesSearch = client.name
        .toLowerCase()
        .includes(searchQuery.trim().toLowerCase());
      const matchesType =
        entityTypeFilter === "All" || client.entityType === entityTypeFilter;
      return matchesSearch && matchesType;
    });

    return sortByCRMPref(filtered, clientSortPref);
  }, [clients, searchQuery, entityTypeFilter, clientSortPref]);

  const {
    page: clientPage,
    setPage: setClientPage,
    rowsPerPage,
    setRowsPerPage,
    paginatedItems: paginatedClients,
  } = useClientListPagination(filteredClients, [searchQuery, entityTypeFilter, clientSortPref]);

  // Use paginated or full list based on screen size
  const displayedClients = isSmallScreen ? paginatedClients : filteredClients;

  const handleMenuClose = () => setMenuAnchorEl(null);

  const convertToCSV = (arr) => {
    if (!arr || arr.length === 0) return "";
    const keys = Object.keys(arr[0]);
    const header = keys.join(",");
    const rows = arr.map((row) =>
      keys.map((key) => JSON.stringify(row[key])).join(","),
    );
    return [header, ...rows].join("\n");
  };

  const handleExportClients = (idsToExport: string[]) => {
    handleMenuClose();
    if (idsToExport.length === 0) {
      return setAlert(
        "Please select at least one client to export.",
        "warning",
      );
    }
    const dataToExport = clients
      .filter((c) => idsToExport.includes(c._id))
      .map(({ _id, name, entityType }) => ({ _id, name, entityType }));

    const csvData = convertToCSV(dataToExport);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "clients.csv");
  };

  const entityTypes = [
    "All",
    "Individual",
    "SoleTrader",
    "Company",
    "Family",
    "Trust",
  ];

  useEffect(() => {
    if (clients.length === 0) return;
    if (selectedClient) {
      const freshData = clients.find((c) => c._id === selectedClient._id);
      if (freshData && freshData !== selectedClient) {
        setSelectedClient(freshData);
      }
      return;
    }
    // Nothing selected yet this mount — restore whatever was selected before the user
    // navigated away (or refreshed), once the list has loaded enough to resolve the id.
    if (hasRestoredSelectionRef.current) return;
    hasRestoredSelectionRef.current = true;
    setIsRestoringSelection(false);
    if (selectedClientId) {
      const restored = clients.find((c) => c._id === selectedClientId);
      if (restored) {
        setSelectedClient(restored);
        fetchClientContacts(restored._id);
      } else {
        setSelectedClientId(null); // stale id (e.g. client since deleted)
      }
    }
  }, [clients, selectedClient, selectedClientId]);

  return (
    <Container>
      <Box sx={{ height: "calc(100vh - 118px)", overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h4" fontWeight={700}>
            Client Management
          </Typography>
          {/* Export Button - Secondary style, placed before Add button */}
          {/* <Button
    variant="outlined"
    size="small"
    onClick={handleMenuOpen}
    endIcon={<ArrowDropDownIcon />}
    sx={{ width: { xs: '100%', sm: 'auto',marginLeft:'62%' } }}
  >
    Export Clients 
  </Button> */}

          {/* Export Menu */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleExportClients(selectedClientIds);
              }}
              disabled={selectedClientIds.length === 0}
            >
              Export Selected ({selectedClientIds.length})
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleExportClients(clients.map((c) => c._id));
              }}
            >
              Export All
            </MenuItem>
          </Menu>

          {/* Add Button - Primary style with ID for AI targeting */}
          <Button
            id="add-client-button"
            variant="contained"
            size="small"
            onClick={() => {
              setClientForModal(null);
              setIsClientModalOpen(true);
            }}
            startIcon={<AddIcon />}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Add Client
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <StatCard
            title="Total Clients"
            value={clients.length}
            icon={<PeopleIcon />}
          />
          <StatCard
            title="Companies"
            value={clients.filter((c) => c.entityType === "Company").length}
            icon={<ApartmentIcon />}
          />
          <StatCard
            title="Individuals"
            value={clients.filter((c) => c.entityType === "Individual").length}
            icon={<PersonIcon />}
          />
          <StatCard
            title="New This Month"
            value={
              clients.filter(
                (c) => new Date(c.createdAt) > new Date(new Date().setDate(1)),
              ).length
            }
            icon={<TrendingUpIcon />}
          />
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 0 },
            height: { xs: "auto", md: "calc(100vh - 258px)" },
          }}
        >
          <Box
            ref={listPanelRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              // Fixed, user-draggable width once side-by-side; full width when stacked.
              // The drag writes an inline width straight onto this element, so when the
              // layout stacks we must override it back to 100% (inline styles beat sx).
              width: isStacked ? "100% !important" : `${listWidth}px`,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: "12px",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                height: { xs: "auto", md: "calc(100vh - 308px)" },
                minHeight: { xs: "500px", md: "auto" },
              }}
            >
              <Stack direction="row" spacing={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={entityTypeFilter}
                    label="Type"
                    onChange={(e) => setEntityTypeFilter(e.target.value)}
                  >
                    {entityTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Sort clients">
                  <IconButton
                    size="small"
                    onClick={(e) => setSortMenuAnchorEl(e.currentTarget)}
                    color={
                      clientSortPref.field !== CRM_SORT_DEFAULT.field ||
                      clientSortPref.order !== CRM_SORT_DEFAULT.order
                        ? "primary"
                        : "default"
                    }
                  >
                    <SwapVertIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={sortMenuAnchorEl}
                  open={Boolean(sortMenuAnchorEl)}
                  onClose={() => setSortMenuAnchorEl(null)}
                >
                  {CRM_SORT_OPTIONS.map((option) => (
                    <MenuItem
                      key={`${option.field}-${option.order}`}
                      selected={
                        clientSortPref.field === option.field &&
                        clientSortPref.order === option.order
                      }
                      onClick={() => {
                        setClientSortPref({ field: option.field, order: option.order });
                        setSortMenuAnchorEl(null);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Stack>
              {/* Header bar — outside scroll; pr hard-coded to 8px + 15px scrollbar so icons align with rows */}
              {!isClientsLoading && displayedClients.length > 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  py={1}
                  pl={1}
                  sx={{
                    pr: "17px !important",
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    mb: 1,
                    gap: 0.5,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={
                      displayedClients.length > 0 &&
                      selectedClientIds.length === filteredClients.length
                    }
                    indeterminate={
                      selectedClientIds.length > 0 &&
                      selectedClientIds.length < filteredClients.length
                    }
                    onChange={(e) => handleSelectAllClients(e, filteredClients)}
                  />
                  <Box flexGrow={1} px={1}>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {selectedClientIds.length} of {filteredClients.length} selected
                    </Typography>
                  </Box>

                  {/* Always 3 icon slots — matches row's View + Edit + Delete columns */}
                  <Box display="flex" gap={0.25} ml={0.5}>
                    {selectedClientIds.length > 0 ? (
                      <Tooltip title={`Export ${selectedClientIds.length} selected`}>
                        <IconButton size="small" color="primary" onClick={() => handleExportClients(selectedClientIds)}>
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" disabled sx={{ visibility: "hidden", pointerEvents: "none" }}>
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                    {selectedClientIds.length > 0 ? (
                      <Tooltip title={`Delete ${selectedClientIds.length} selected`}>
                        <IconButton size="small" color="error" onClick={() => { setIsDeleteClientDialogOpen(true); setClientForModal({ name: `${selectedClientIds.length} client(s)` } as any); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" disabled sx={{ visibility: "hidden", pointerEvents: "none" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    {/* Spacer matching row's third icon (Delete) column */}
                    <IconButton size="small" disabled sx={{ visibility: "hidden", pointerEvents: "none" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}

              <Box sx={{ overflowY: "auto", flexGrow: 1, minHeight: 0 }}>
                {isClientsLoading ? (
                  <TableSkeleton rows={5} cols={6} />
                ) : clients.length === 0 ? (
                  <EmptyState
                    icon={<WorkOutlineIcon sx={{ fontSize: 48 }} />}
                    title="No clients yet"
                    description="Add your first client to start tracking your relationships."
                    primaryAction={{
                      label: '+ Add Client',
                      onClick: () => { setClientForModal(null); setIsClientModalOpen(true); },
                    }}
                  />
                ) : displayedClients.length > 0 ? (
                  <List disablePadding>
                      {displayedClients.map((client) => {
                        // ── Renewal status for this client ──────────────
                        const renewal = getRenewalStatus(getClientReferenceDate(client));
                        const isChecked = selectedClientIds.includes(client._id);

                        return (
                          <ListItem
                            key={client._id}
                            disablePadding
                            sx={{ mb: 0.5 }}
                          >
                            {/*
                             * ── Renewal Tooltip wrapper ──────────────────
                             * If the client is in a renewal window, wrap the
                             * button with a descriptive tooltip. The `title`
                             * uses pre-line whitespace so \n renders as a
                             * new line inside the MUI tooltip.
                             */}
                            <Tooltip
                              title={
                                renewal ? (
                                  <Box>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                      <CalendarMonthIcon sx={{ fontSize: 14 }} />
                                      <Typography variant="caption" fontWeight={700}>
                                        {renewal.label}
                                      </Typography>
                                    </Box>
                                    {renewal.tooltip.split("\n").map((line, i) => (
                                      <Typography key={i} variant="caption" display="block">
                                        {line}
                                      </Typography>
                                    ))}
                                  </Box>
                                ) : ""
                              }
                              placement="right"
                              arrow
                              disableHoverListener={!renewal}
                              disableFocusListener={!renewal}
                              disableTouchListener={!renewal}
                            >
                              <ListItemButton
                                selected={selectedClient?._id === client._id}
                                onClick={() => handleSelectClient(client)}
                                onDoubleClick={() => navigate(`/client/${client._id}`)}
                                sx={{
                                  pr: 1,
                                  // Renewal colour coding. Selected state layers the theme's own
                                  // selected tint on top via backgroundImage rather than just
                                  // darkening the renewal tint — a brightness filter on an
                                  // already-pale overlay reads as nearly identical to unselected,
                                  // which is what made a deselected renewal row look "stuck".
                                  ...(renewal && {
                                    borderLeft: `3px solid ${renewal.borderColor}`,
                                    bgcolor: renewal.bgColor,
                                    "&:hover": {
                                      bgcolor: renewal.bgColor,
                                      filter: "brightness(0.96)",
                                    },
                                    "&.Mui-selected": {
                                      bgcolor: renewal.bgColor,
                                      backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.primary.main, 0.16)})`,
                                    },
                                    "&.Mui-selected:hover": {
                                      bgcolor: renewal.bgColor,
                                      backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.22)}, ${alpha(theme.palette.primary.main, 0.22)})`,
                                    },
                                  }),
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={isChecked}
                                  onChange={(e) => handleToggleClientSelection(e, client._id)}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0.5, mr: 0.5 }}
                                />
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{ color: renewal ? renewal.color : "inherit" }}
                                      >
                                        {client.name}
                                      </Typography>
                                      {renewal && (
                                        <Box
                                          sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: "50%",
                                            bgcolor: renewal.borderColor,
                                            flexShrink: 0,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={client.entityType}
                                />
                                {/* Actions — always visible */}
                                <Box display="flex" gap={0.25} flexShrink={0} ml={0.5}>
                                  <Tooltip title="Open full profile">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); navigate(`/client/${client._id}`); }}
                                    >
                                      <ViewIcon fontSize="small" color="primary" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handleOpenModal("editClient", client); }}
                                    >
                                      <EditIcon fontSize="small" color="primary" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handleOpenModal("deleteClient", client); }}
                                    >
                                      <DeleteIcon fontSize="small" color="error" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ListItemButton>
                            </Tooltip>
                          </ListItem>
                        );
                      })}
                    </List>
                ) : (
                  <NoDataFound
                    title="No Clients Found"
                    subtitle="Try adjusting your filters or add a new client."
                  />
                )}
              </Box>

              {/* Pagination Controls for smaller screens */}
              {isSmallScreen && filteredClients.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pt: 2,
                  mt: "auto",
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing{" "}
                  <strong>{filteredClients.length === 0 ? 0 : (clientPage - 1) * rowsPerPage + 1}</strong>
                  {" "}to{" "}
                  <strong>{Math.min(clientPage * rowsPerPage, filteredClients.length)}</strong>
                  {" "}of{" "}
                  <strong>{filteredClients.length}</strong>
                  {" "}results
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <FormControl size="small" variant="outlined" sx={{ minWidth: 61 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => { setRowsPerPage(Number(e.target.value)); setClientPage(1); }}
                      displayEmpty
                      inputProps={{ "aria-label": "Rows per page" }}
                      sx={{ ".MuiOutlinedInput-notchedOutline": { border: 0 } }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => setClientPage((p) => Math.max(1, p - 1))}
                    disabled={clientPage === 1}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setClientPage((p) => Math.min(Math.ceil(filteredClients.length / rowsPerPage), p + 1))}
                    disabled={clientPage >= Math.ceil(filteredClients.length / rowsPerPage)}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
            </Paper>
          </Box>

          {!isStacked && <ResizeHandle onMouseDown={handleResizeStart} gutter={9} />}

          <Box sx={{ flex: 1, minWidth: 0, height: { xs: "auto", md: "100%" } }}>
            <ClientDetailPanel
              client={selectedClient}
              contacts={clientContacts}
              isLoading={isContactsLoading}
              isRestoringSelection={isRestoringSelection}
              onOpenModal={handleOpenModal}
            />
          </Box>
        </Box>
      </Box>

      {/* REUSABLE CLIENT MODAL (Handles both Add and Edit) */}
      <ClientModal
        open={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onSave={() => {
          fetchClientsData();
          // If we were editing the currently selected client, update the view
          if (
            selectedClient &&
            clientForModal &&
            selectedClient._id === clientForModal._id
          ) {
            // We might need to refetch the specific client or just rely on the list update
            // Ideally, fetchClientsData updates the list, but selectedClient might need a refresh
          }
        }}
        clientData={clientForModal} // null = Add, Object = Edit
      />

      <ContactModal
        open={isContactModalOpen}
        onClose={() => {
          // Close contact modal on cancel without triggering the Link flow.
          setIsContactModalOpen(false);
          setCurrentRelationshipData(null);
          // Clear the returnToLinkMode flag so the Link modal isn't reopened on cancel
          setReturnToLinkMode(false);
        }}
        onSave={(newContact) => {
          // Refresh linked contacts if a client is selected (in case we edited a linked contact)
          if (selectedClient?._id) {
            fetchClientContacts(selectedClient._id);
          }

          // Check if we need to return to Link Modal with new contact selected
          if (returnToLinkMode) {
            if (newContact) {
              setNewlyCreatedContact(newContact); // Store the newly created contact
            }
            setIsContactModalOpen(false); // Close create
            setIsLinkModalOpen(true); // Re-open link (this will auto-fetch the new contact list)
            setReturnToLinkMode(false); // Reset flag
          } else {
            // If just editing, close the modal
            setIsContactModalOpen(false);
          }
        }}
        contactData={contactForModal}
        relationshipData={currentRelationshipData} // Relationship info for linked contacts
        allLinkedContacts={clientContacts} // To check for existing primary contacts
        entityType={selectedClient?.entityType}
      />

      <ConfirmDeleteModal
        open={isDeleteClientDialogOpen}
        entityType="client"
        entityName={clientForModal?.name ?? ''}
        onConfirm={handleDeleteClient}
        onCancel={() => setIsDeleteClientDialogOpen(false)}
      />

      {/* Keep existing Link Modal */}
      {clientForModal && (
        <ClientLinkContactModal
          open={isLinkModalOpen}
          onClose={() => {
            setIsLinkModalOpen(false);
            setNewlyCreatedContact(null); // Clear on close
          }}
          onSave={() => fetchClientContacts(selectedClient?._id || null)}
          client={clientForModal}
          orgId={login.data.orgId}
          userId={login.data.id}
          existingLinkedContacts={clientContacts}
          onCreateNew={handleSwitchToCreateContact}
          preSelectedContact={newlyCreatedContact} // Pass the newly created contact
        />
      )}

      <UnlinkConfirmationDialog
        open={isUnlinkConfirmOpen}
        onClose={() => setIsUnlinkConfirmOpen(false)}
        onConfirm={handleUnlinkContact}
        contactName={contactForModal?.name}
      />
    </Container>
  );
};

export default ClientsPage;